import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 aria-disabled:opacity-50 aria-disabled:cursor-default [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        /* `dark:hover:text-foreground` is NOT upstream shadcn and must not be
           "cleaned up" back to it. Both variants override the dark-mode hover
           SURFACE away from `--accent` while upstream leaves the hover TEXT at
           `--accent-foreground` — a pairing that only works because upstream's
           dark `--accent-foreground` is near-white. This palette deliberately
           inverts it (`app/globals.css` .dark: near-black, so it clears 4.5:1 on
           the bright orange `--accent`; see the note there), which left both
           hovers painting near-black text on a near-black wash. Measured with
           the repo's culori pipeline, dark mode:

             outline  hover surface = white/15% × /50 ≈ 7.5% over --background
               --accent-foreground   1.17:1   FAIL (the reported black-on-black)
               --foreground         15.84:1   pass
             ghost    hover surface = --accent/50 over --background
               --accent-foreground   2.43:1   FAIL
               --foreground          7.64:1   pass

           `--foreground`/`--background` is a BLOCKING pair in
           `lib/branding/required-pairs.ts`, so this holds for branded tenants
           too. `components/ui/button.contrast.test.ts` measures all four.
           Everything that keeps `hover:bg-accent` in dark mode (dropdown/select
           items, command rows, the context chip) stays on
           `--accent-foreground` at 6.83:1 and is untouched. */
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 dark:hover:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 dark:hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /**
     * The action this button triggers is in flight — level **E0** of the
     * action-feedback norm (spec `2026-08-03-action-feedback-system.md` §2.2,
     * §A7). Renders a spinner, marks the control `aria-busy`, and refuses
     * activation.
     */
    loading?: boolean
  }

/**
 * The pending affordance, built once (§A7).
 *
 * The tree has 141 `disabled={…isPending…}` call sites across 140 files and
 * roughly 110 of them show no spinner at all; `aria-busy` occurs 13 times and
 * never on a button. `loading` exists so the B-batches stop hand-rolling a
 * fourth dialect, and it encodes three decisions:
 *
 *  1. **The accessible name survives.** The label stays rendered and the spinner
 *     is `aria-hidden`, so "Bestätigen" does not become an unnamed button
 *     mid-action — the state is carried by `aria-busy`, which is what an AT
 *     reads it from.
 *  2. **`aria-disabled`, never `disabled`.** `disabled` removes the control from
 *     the tab order *while the user is standing on it*: focus falls to `<body>`
 *     and nothing is announced, which is the single most common keyboard defect
 *     in a pending state. `aria-disabled` keeps focus, keeps the name, and
 *     announces "dimmed"/"unavailable". `components/isms/detail/AttachmentsArea.tsx:172`
 *     already reached for this by hand; here it is the rule.
 *     A consequence, deliberately: while `loading` is true the native `disabled`
 *     attribute is **suppressed** even if the caller also passed
 *     `disabled={isPending}` — the common existing spelling. Activation is
 *     blocked either way (below), so the only thing that changes is that the
 *     keyboard user keeps their place.
 *  3. **Activation is blocked in the handler, not by the DOM.** `aria-disabled`
 *     is advisory: it tells assistive tech, it does not stop a click. The click
 *     handler therefore `preventDefault()`s — which, on a `type="submit"`
 *     button, also cancels the implicit form submit — and `stopPropagation()`s
 *     so a row-level handler cannot fire instead.
 *
 * `asChild` renders a `Slot`, which accepts exactly one child, so no spinner is
 * injected in that mode; the ARIA state and the activation guard still apply.
 */
function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  disabled,
  onClick,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button"

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (loading) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      onClick?.(event)
    },
    [loading, onClick]
  )

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={loading ? "true" : undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      // See (2): `loading` deliberately wins over a caller-supplied `disabled`.
      // `undefined` (attribute absent), not `false`, so nothing is emitted onto
      // a slotted child that has no `disabled` attribute to begin with.
      disabled={loading ? undefined : disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...props}
    >
      {/* `asChild` hands the children straight through: `Slot` requires exactly
          one element child, and even a `false` from a short-circuited spinner
          would make this an array of two and throw. */}
      {asChild ? (
        children
      ) : (
        <>
          {loading && (
            // `motion-reduce:animate-none` on top of the global reduced-motion
            // block in `app/globals.css`, so the intent is readable here too.
            <Loader2
              className="size-4 animate-spin motion-reduce:animate-none"
              aria-hidden
              data-slot="button-spinner"
            />
          )}
          {children}
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
